import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle2, MessageCircle, Share2, Building2 } from "lucide-react";
import { WhatsAppChatButton, SocialLinks } from "../../components/app/WhatsApp";
import { CONTACT } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { SectionHeading } from "../../components/marketing/Blocks";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = (e) => {
    e.preventDefault();
    const existing = JSON.parse(localStorage.getItem("kt_contact_messages") || "[]");
    localStorage.setItem("kt_contact_messages", JSON.stringify([...existing, { ...form, at: new Date().toISOString() }]));
    setSent(true);
  };

  return (
    <section className="kt-container py-16 lg:py-24">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <SectionHeading eyebrow="Contact" title="Talk to a human." lead="Questions about a ride, a partnership or your account? We reply within one working day." />
          <ul className="mt-10 space-y-6">
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand"><Building2 className="h-5 w-5" /></span>
              <div><p className="font-semibold text-ink">Company legal name</p><p className="text-sm text-ink-soft" data-testid="contact-legal-name">{CONTACT.legalName}</p></div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand"><MapPin className="h-5 w-5" /></span>
              <div><p className="font-semibold text-ink">Office</p><p className="text-sm text-ink-soft" data-testid="contact-address">{CONTACT.addressLines[0]},<br />{CONTACT.addressLines[1]}</p></div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand"><Mail className="h-5 w-5" /></span>
              <div><p className="font-semibold text-ink">Email</p><a href={`mailto:${CONTACT.email}`} className="text-sm text-ink-soft hover:text-brand" data-testid="contact-email">{CONTACT.email}</a></div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand"><Phone className="h-5 w-5" /></span>
              <div><p className="font-semibold text-ink">Support line</p><a href={CONTACT.phoneHref} className="text-sm font-medium text-ink-soft hover:text-brand" data-testid="contact-phone">{CONTACT.phone}</a><p className="text-xs text-ink-muted">Mon to Sat, 7am to 10pm IST</p></div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#25D366]/15 text-[#128C7E]"><MessageCircle className="h-5 w-5" /></span>
              <div><p className="font-semibold text-ink">WhatsApp</p><p className="mb-2 text-sm text-ink-soft">{CONTACT.phone} · Fastest way to reach us. Usually replies within minutes.</p><WhatsAppChatButton text="Hi KohimaTaxi, I need help with " /></div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sunny-50 text-sunny-600"><MapPin className="h-5 w-5" /></span>
              <div><p className="font-semibold text-ink">Find us on Google</p><p className="mb-2 text-sm text-ink-soft">See our Google Business profile, photos and reviews.</p><a href={CONTACT.googleProfile} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600" data-testid="contact-google-profile">Open Google profile</a></div>
            </li>
            <li className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand"><Share2 className="h-5 w-5" /></span>
              <div><p className="font-semibold text-ink">Follow us</p><p className="mb-2 text-sm text-ink-soft">Offers, new restaurants and city updates.</p><SocialLinks testId="contact-social" /></div>
            </li>
          </ul>
        </div>
        <div className="kt-card p-8">
          {sent ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-graphite" />
              <h3 className="mt-4 text-xl font-semibold text-ink">Message received</h3>
              <p className="mt-2 max-w-sm text-sm text-ink-soft">Thanks, {form.name || "friend"}. We will get back to you at {form.email}.</p>
              <Button variant="outline" className="mt-6 rounded-full" onClick={() => { setSent(false); setForm({ name: "", email: "", topic: "general", message: "" }); }}>Send another</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2"><Label htmlFor="c-name">Name</Label><Input id="c-name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" /></div>
                <div className="space-y-2"><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email" /></div>
              </div>
              <div className="space-y-2">
                <Label>Topic</Label>
                <Select value={form.topic} onValueChange={set("topic")}>
                  <SelectTrigger data-testid="contact-topic"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General question</SelectItem>
                    <SelectItem value="ride">A ride or order</SelectItem>
                    <SelectItem value="driver">Becoming a driver</SelectItem>
                    <SelectItem value="restaurant">Restaurant partnership</SelectItem>
                    <SelectItem value="press">Press</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label htmlFor="c-msg">Message</Label><Textarea id="c-msg" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" data-testid="contact-message" /></div>
              <Button type="submit" className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="contact-submit"><Send className="mr-2 h-4 w-4" /> Send message</Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
